import '@egjs/react-flicking/dist/flicking.css';

import { BLUR_URL, imageOpt } from '@/utils/images';
import { Box, Flex, Grid, Group, createStyles } from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { ITechstack, ITechstackCompanyList } from '@/types/techstack';
import { IconChevronDown, IconLogin } from '@tabler/icons-react';
import { groupBy, isDark, redirectToOneIdPage } from '@/utils/utils';
import { useEffect, useState } from 'react';

import { COMPANY_LOGO_ENDPOINT } from '@/config/endpoint';
import CustomImage from '@/components/CustomImage';
import DMText from '@/components/DMText';
import Flicking from '@egjs/react-flicking';
import { isSignedIn } from '@/config/globalStateControl';
import { mixpanelTrack } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { useTechStackFreeCountCheck } from '../hook/useTechstackMethod';

const useTechstackDetailCompaniesStyle = createStyles((theme, dark: any) => ({
  menuItem: {
    padding: '0px 16px',
    borderRadius: '5rem',
    border: `1.2px solid ${themeVariables.lineDark.light300(dark?.dark)}`,
    color: themeVariables.fontColors.extraLight,
    cursor: 'pointer',
    gap: 12,
    marginRight: 8,
    fontSize: 15,
    '&:hover': {
      color: themeVariables.fontColors.extraLight,
      border: `1.2px solid ${theme.colors.blue[3]}`,
    },
    fontWeight: 500,
    lineHeight: '22px',
    height: 40,
    [theme.fn.smallerThan('sm')]: {
      padding: '0px 12px',
      fontSize: 14,
      lineHeight: '20px',
      height: 32,
    },
  },

  activeMenuItem: {
    borderRadius: '5rem',
    cursor: 'pointer',
    gap: 12,
    color: themeVariables.fontColors.extraLight,
    border: `1.2px solid ${theme.colors.blue[4]}`,
    marginRight: 8,
    padding: '0px 16px',
    fontSize: 15,
    fontWeight: 500,
    lineHeight: '22px',
    height: 40,
    [theme.fn.smallerThan('sm')]: {
      padding: '0px 12px',
      fontSize: 14,
      lineHeight: '20px',
      height: 32,
    },
  },

  companyImageWrapper: {
    overflow: 'hidden',
    borderRadius: 10,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    minWidth: 48,
    height: 48,
    [theme.fn.smallerThan('sm')]: {
      minWidth: 40,
      height: 40,
    },
  },

  companyCardWrapper: {
    gap: 24,
    flexWrap: 'nowrap',
    cursor: 'pointer',
    ':hover': {
      div: {
        fontWeight: 500,
      },
    },
    [theme.fn.smallerThan('sm')]: {
      gap: 12,
    },
  },

  companyImage: {
    width: 48,
    height: 48,
    [theme.fn.smallerThan('sm')]: {
      width: 40,
      height: 40,
    },
  },

  companyText: {
    fontSize: 16,
    fontWeight: 400,
    transition: '150ms all ease',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 15,
      lineHeight: '20px',
    },
  },

  blurBackground: {
    position: 'absolute',
    height: '110%',
    width: '104%',
    top: -10,
    left: -10,
    backdropFilter: 'blur(8px);',
  },

  blurButton: {
    position: 'absolute',
    zIndex: 10,
    left: '50%',
    top: '40%',
    transform: 'translateX(-50%)',
  },

  moreBtn: {
    gap: 4,
    margin: '40px auto 0px auto',
    border: `1px solid ${themeVariables.line.normal}`,
    borderRadius: 8,
    width: 150,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 15,
    fontWeight: 500,
    marginTop: 30,
    cursor: 'pointer',
    ':hover': {
      border: `1px solid ${themeVariables.colors.light400}`,
    },
    ':active': {
      transform: 'scale(99%)',
    },
    [theme.fn.smallerThan('sm')]: {
      width: 125,
      height: 32,
      fontSize: 13,
    },
  },
}));

const TechstackDetailCompanies = ({ data }: { data: ITechstack }) => {
  const { dark } = isDark();
  const { classes } = useTechstackDetailCompaniesStyle({ dark });

  const [selectedCategory, setSelectedCategory] = useState<string>('');

  return (
    <>
      <Flex justify={'space-between'} mt={56}>
        <DMText
          size={22}
          weight={600}
          lts={-0.4}
          dColor={themeVariables.darkMode.normal}
        >
          사용 기업
        </DMText>
      </Flex>
      <Box mt={24} mb={40}>
        <Flicking
          bound
          horizontal
          align={'prev'}
          renderOnlyVisible
          moveType={'snap'}
          renderOnSameKey
          preventClickOnDrag
          preventDefaultOnDrag
          useResizeObserver={false}
        >
          {Object.entries(groupBy(data?.company_list, 'category')).map(
            ([category]) => (
              <Group
                key={category}
                className={
                  selectedCategory === category
                    ? classes.activeMenuItem
                    : classes.menuItem
                }
                onClick={() =>
                  setSelectedCategory(
                    category == selectedCategory ? '' : category
                  )
                }
              >
                <DMText dColor={themeVariables.darkMode.normal}>
                  {category}
                </DMText>
              </Group>
            )
          )}
        </Flicking>
      </Box>
      <CompanyList data={data} selectedCategory={selectedCategory} />
    </>
  );
};

export default TechstackDetailCompanies;

const CompanyList = ({ data, selectedCategory }) => {
  const { dark } = isDark();
  const { push } = useRouter();
  const { classes } = useTechstackDetailCompaniesStyle({ dark });

  const [expanded, setExpanded] = useState<boolean>(false);

  const id = data?.techstack?.id;
  const isLogin = useAtomValue(isSignedIn);
  const disabled = useTechStackFreeCountCheck(id);

  const companies: any = [];

  useEffect(() => {
    setExpanded(false);
  }, [selectedCategory]);

  for (const company of data?.company_list as ITechstackCompanyList[]) {
    if (!selectedCategory) {
      companies.push(company);
    } else if (selectedCategory == company.category) {
      companies.push(company);
    }
  }

  const loginCheckCompanies =
    !isLogin && disabled ? companies?.slice(0, 18) : companies;

  return (
    <Box pos={'relative'}>
      {!isLogin && disabled && <LoginButton classes={classes} />}
      <Grid columns={24} gutter={20}>
        {loginCheckCompanies?.slice(0, expanded ? 1000 : 18)?.map((company) => (
          <Grid.Col
            key={company.id}
            xl={4}
            md={6}
            sm={8}
            xs={12}
            maw={'50%'}
            onClick={() => {
              mixpanelTrack('TechstackDetailPage_CompanyIcon', 'Clicked', {
                id: id,
              });
              push(`/company/detail/${company.id}`);
            }}
          >
            <Group id={`${company.id}`} className={classes.companyCardWrapper}>
              <Box className={classes.companyImageWrapper}>
                <CustomImage
                  className={classes.companyImage}
                  width={48}
                  height={48}
                  src={imageOpt(
                    `${COMPANY_LOGO_ENDPOINT}/${company.id}.png`,
                    720
                  )}
                  placeholder={'blur'}
                  blurDataURL={BLUR_URL}
                  alt="techstack-logo"
                  priority
                  errSrc={BLUR_URL}
                  style={{
                    backgroundColor: '#fff',
                  }}
                />
              </Box>
              <DMText
                className={classes.companyText}
                dColor={themeVariables.darkMode.normal}
              >
                {company.name}
              </DMText>
            </Group>
          </Grid.Col>
        ))}
      </Grid>
      {!expanded && (companies?.length || 0) > 18 && (
        <Flex className={classes.moreBtn} onClick={() => setExpanded(true)}>
          <DMText dColor={themeVariables.darkMode.normal}>더 보기</DMText>
          <IconChevronDown
            size={20}
            color={themeVariables.darkMode.normal(dark)}
          />
        </Flex>
      )}
    </Box>
  );
};

const LoginButton = ({ classes }) => {
  return (
    <>
      <div className={classes.blurBackground} />
      <div className={classes.blurButton}>
        <Button
          type={ButtonTypes.whiteRoundedRaised}
          leftIcon={<IconLogin size={18} />}
          onClick={() => redirectToOneIdPage()}
        >
          로그인하고 확인하기
        </Button>
      </div>
    </>
  );
};
